import { Box, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	ButtonFilled,
	ButtonLink,
	ButtonOutline,
	ButtonPlain,
	Global,
} from "../../../components/icons/Icons";
// import { ANIMATIONS } from "../../../constants";
import {
	Background,
	Border,
	BoxShadow,
	ColorPicker,
	Dimensions,
	IconPicker,
	PopoverDrawer,
	// Position,
	Select,
	Slider,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	UrlInput,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";
import { isCSSVariable } from "../../../utils";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			clientId,
			link,
			style,
			size,
			padding,
			typography,
			icon,
			iconPosition,
			iconGap,
			iconSize,
			color1,
			color2,
			background1,
			background2,
			hoverBackground1,
			hoverBackground2,
			border1,
			hoverBorder1,
			border2,
			hoverBorder2,
			boxShadow,
			boxShadowHover,
			blockMargin,
			blockPadding,
			blockZIndex,
			cssID,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
			className,
			width,
			buttonAnimation,
			positionProperty,
			blockHTML,
			hoverColor1,
			hoverColor2,
		},
		setAttributes,
	} = props;

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("Layout", "magazine-blocks")} initialOpen>
					<UrlInput
						label={__("URL", "magazine-blocks")}
						onChange={(val) => setAttributes({ link: val })}
						value={link}
						placeholder="https://"
						newTab
						noFollow
					/>

					<Slider
						label={__("Width", "magazine-blocks")}
						value={width}
						onChange={(val) => setAttributes({ width: val })}
						min={25}
						max={100}
						step={5}
						forceShowUnit
						resetAttributeKey={"width"}
						responsive
						defaultUnit="%"
					/>

					<ToggleButtonGroup
						value={style}
						onChange={(v) => {
							setAttributes({ style: v });
						}}
						label={__("Type", "magazine-blocks")}
						resetAttributeKey="style"
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
								},
								svg: {
									fill: "#909090 !important",
									width: "40px !important",
								},
								".chakra-button[data-active] svg, .chakra-button:active svg":
									{
										fill: "#ffffff !important",
									},
							},
						}}
					>
						<ToggleButton value="filled" title="Filled">
							<ButtonFilled />
						</ToggleButton>
						<ToggleButton value="outline" title="Outline">
							<ButtonOutline />
						</ToggleButton>
						<ToggleButton value="plain" title="Plain">
							<ButtonPlain />
						</ToggleButton>
						<ToggleButton value="link" title="Link">
							<ButtonLink />
						</ToggleButton>
					</ToggleButtonGroup>

					{("filled" === style || "outline" === style) && (
						<>
							<Box my="18px">
								<ToggleButtonGroup
									value={size}
									onChange={(v) => {
										setAttributes({ size: v });
									}}
									label={__("Size", "magazine-blocks")}
									resetAttributeKey="size"
									groupProps={{
										sx: {
											".chakra-button": {
												minH: "32px",
												svg: {
													w: 6,
													h: 6,
												},
											},
										},
									}}
								>
									<ToggleButton value="small">
										Small
									</ToggleButton>
									<ToggleButton value="medium">
										Medium
									</ToggleButton>
									<ToggleButton value="large">
										Large
									</ToggleButton>
									<ToggleButton value="custom">
										Custom
									</ToggleButton>
								</ToggleButtonGroup>
							</Box>

							{size && "custom" === size && (
								<Dimensions
									value={padding || {}}
									responsive
									label={__("Padding", "magazine-blocks")}
									defaultUnit="px"
									units={["px", "rem", "em", "%"]}
									onChange={(val) =>
										setAttributes({ padding: val })
									}
									type="padding"
								/>
							)}
						</>
					)}
				</Panel>
				<Panel title={__("Icons", "magazine-blocks")}>
					<>
						<IconPicker
							hasToggle={true}
							value={icon || {}}
							onChange={(val) => setAttributes({ icon: val })}
						/>

						{icon?.enable && (
							<>
								<ToggleButtonGroup
									value={iconPosition}
									onChange={(v) => {
										setAttributes({ iconPosition: v });
									}}
									label={__("Position", "magazine-blocks")}
									groupProps={{
										sx: {
											".chakra-button": {
												minH: "32px",
												svg: {
													w: 6,
													h: 6,
												},
												"&[data-active]": {
													svg: {
														fill: "#fff !important",
													},
												},
											},
										},
									}}
								>
									<ToggleButton value="left">
										{__("Before Text", "magazine-blocks")}
									</ToggleButton>
									<ToggleButton value="right">
										{__("After Text", "magazine-blocks")}
									</ToggleButton>
								</ToggleButtonGroup>

								<Slider
									resetAttributeKey="iconSize"
									label={__("Size", "magazine-blocks")}
									min={0}
									max={50}
									value={iconSize}
									onChange={(val: any) =>
										setAttributes({ iconSize: val })
									}
									responsive
									units={["px", "em", "%"]}
								/>

								<Slider
									resetAttributeKey="iconGap"
									label={__("Gap", "magazine-blocks")}
									min={0}
									max={60}
									value={iconGap}
									onChange={(val: any) =>
										setAttributes({ iconGap: val })
									}
									responsive
									units={["px", "em", "%"]}
								/>
							</>
						)}
					</>
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel title={__("Text", "magazine-blocks")} initialOpen>
					<Typography
						onChange={(val) => setAttributes({ typography: val })}
						value={typography}
						resetAttributeKey="typography"
					/>

					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => {
							const Trigger = () => (
								<PopoverDrawer.Trigger {...props}>
									<ZStack
										items={[
											{
												color:
													"filled" === style
														? color1
														: color2,
												id: "color",
											},
											{
												color:
													"filled" === style
														? hoverColor1
														: hoverColor2,
												id: "color",
											},
										]}
									/>
								</PopoverDrawer.Trigger>
							);
							if (
								isCSSVariable(color1) ||
								isCSSVariable(color2) ||
								isCSSVariable(hoverColor1) ||
								isCSSVariable(hoverColor2)
							) {
								return (
									<Box position="relative">
										<Trigger />
										<Global
											position="absolute"
											fill="primary.500"
											height="14px"
											width="14px"
											top="-5px"
											left="-10px"
											bg="white"
											borderRadius="full"
										/>
									</Box>
								);
							}
							return <Trigger />;
						}}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={
											"filled" === style ? color1 : color2
										}
										resetKey={
											"filled" === style
												? "color1"
												: "color2"
										}
										onReset={(v) => {
											"filled" === style
												? setAttributes({ color1: v })
												: setAttributes({ color2: v });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										onChange={(val) =>
											"filled" === style
												? setAttributes({ color1: val })
												: setAttributes({ color2: val })
										}
										label={__("Color", "magazine-blocks")}
										value={
											"filled" === style ? color1 : color2
										}
										resetKey={
											"filled" === style
												? "color1"
												: "color2"
										}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={
											"filled" === style
												? hoverColor1
												: hoverColor2
										}
										resetKey={
											"filled" === style
												? "hoverColor1"
												: "hoverColor2"
										}
										onReset={(v) => {
											"filled" === style
												? setAttributes({
														hoverColor1: v,
												  })
												: setAttributes({
														hoverColor2: v,
												  });
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<ColorPicker
										onChange={(val) =>
											"filled" === style
												? setAttributes({
														hoverColor1: val,
												  })
												: setAttributes({
														hoverColor2: val,
												  })
										}
										label={__("Color", "magazine-blocks")}
										value={
											"filled" === style
												? hoverColor1
												: hoverColor2
										}
										resetKey={
											"filled" === style
												? "hoverColor1"
												: "hoverColor2"
										}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Background", "magazine-blocks")}>
					<PopoverDrawer label={__("Type", "magazine-blocks")}>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
								<Tab>{__("Hover", "magazine-blocks")}</Tab>
							</TabList>
							<TabPanels>
								<TabPanel>
									<Reset
										saved={
											"filled" === style
												? background1
												: background2
										}
										resetKey={
											"filled" === style
												? "background1"
												: "background2"
										}
										onReset={(v) => {
											{
												"filled" === style
													? setAttributes({
															background1: v,
													  })
													: setAttributes({
															background2: v,
													  });
											}
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={
											"filled" === style
												? background1
												: background2
										}
										onChange={(v) => {
											{
												"filled" === style
													? setAttributes({
															background1: v,
													  })
													: setAttributes({
															background2: v,
													  });
											}
										}}
									/>
								</TabPanel>
								<TabPanel>
									<Reset
										saved={
											"filled" === style
												? hoverBackground1
												: hoverBackground2
										}
										resetKey={
											"filled" === style
												? "hoverBackground1"
												: "hoverBackground2"
										}
										onReset={(v) => {
											{
												"filled" === style
													? setAttributes({
															hoverBackground1: v,
													  })
													: setAttributes({
															hoverBackground2: v,
													  });
											}
										}}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									/>
									<Background
										value={
											"filled" === style
												? hoverBackground1
												: hoverBackground2
										}
										onChange={(v) => {
											{
												"filled" === style
													? setAttributes({
															hoverBackground1: v,
													  })
													: setAttributes({
															hoverBackground2: v,
													  });
											}
										}}
									/>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
				<Panel title={__("Border", "magazine-blocks")}>
					<Tabs variant="border">
						<TabList>
							<Tab>{__("Normal", "magazine-blocks")}</Tab>
							<Tab>{__("Hover", "magazine-blocks")}</Tab>
						</TabList>
						<TabPanels>
							<TabPanel>
								<Border
									resetAttributeKey={
										"filled" === style
											? "border1"
											: "border2"
									}
									value={
										"filled" === style ? border1 : border2
									}
									onChange={(val) =>
										"filled" === style
											? setAttributes({
													border1: val,
											  })
											: setAttributes({
													border2: val,
											  })
									}
								/>
								<BoxShadow
									resetAttributeKey="boxShadow"
									value={boxShadow}
									onChange={(val) =>
										setAttributes({
											boxShadow: val,
										})
									}
								/>
							</TabPanel>
							<TabPanel>
								<Border
									resetAttributeKey={
										"filled" === style
											? "hoverBorder1"
											: "hoverBorder2"
									}
									value={
										"filled" === style
											? hoverBorder1
											: hoverBorder2
									}
									onChange={(val) =>
										"filled" === style
											? setAttributes({
													hoverBorder1: val,
											  })
											: setAttributes({
													hoverBorder2: val,
											  })
									}
								/>
								<BoxShadow
									resetAttributeKey={"boxShadowHover"}
									value={boxShadowHover}
									onChange={(val) =>
										setAttributes({
											boxShadowHover: val,
										})
									}
								/>
							</TabPanel>
						</TabPanels>
					</Tabs>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
					blockZIndex={blockZIndex}
					cssID={cssID}
					className={className}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
